'use client';

import { useState } from 'react';
import s from './SignalSense.module.css';

/* SignalSense landing page — redesigned (Oct 2026).
   Copy/layout come from the approved design export; styles are page-local
   (SignalSense.module.css) and do NOT touch shared tokens/globals.
   The site Navbar/Footer come from the root layout; the page ends with
   <BookDemoSection/> (id="book-demo"), which every "Talk to Customer Success" CTA
   scrolls to. Images live in /public/ai-stack/signalsense/v2/. */

const IMG = '/ai-stack/signalsense/v2';
const GRADIENT = 'linear-gradient(90deg, rgb(0,153,255) 0%, rgb(14,58,255) 100%)';
const BLUE = 'rgb(47,137,255)';
const INK = 'rgb(35,48,72)';
const BODY = 'rgb(71,85,105)';
const BORDER = 'rgb(226,232,240)';

const STATS = [
  ['Intent', 'Labelled on every conversation'],
  ['Patterns', 'Emerging issues, auto-detected'],
  ['Hours', 'Of head start before it trends'],
  ['3-in-1', 'Crisis · influencer · momentum'],
];

const STEPS = [
  ['01', 'Listen everywhere', 'SignalSense works on the conversations Locobuzz already unifies: social platforms, reviews, and digital care channels. No separate pipeline to set up.'],
  ['02', 'Label the intent', 'Each conversation is read for what it means and tagged as a signal: billing error, service delay, login failure, refund query, influencer praise. Spam is discarded.'],
  ['03', 'Detect the cluster', "When one signal accelerates, say 3.4× normal volume in 40 minutes, SignalSense flags an emerging issue with a severity score and the cities where it's building."],
  ['04', 'Predict and prioritise', 'You get one ranked signal with a trend prediction, "likely to trend in ~4 hours", so the right team can fix the root cause or respond before it spreads.'],
];

const KEYWORD_POINTS = [
  'Matches words, misses meaning and anything phrased differently',
  'Buries teams in unranked alerts and false positives',
  'Flags issues only after volume explodes and the story trends',
  'No severity, no geography, no prediction',
];

const SIGNAL_POINTS = [
  'Reads intent on every conversation, with near-zero false positives',
  'Scores severity and surfaces the one emerging threat that actually matters',
  'Shows the geography and the why behind every clustering issue',
  'Predicts when it will trend, giving you the time to get ahead of it',
];

const MOVES = [
  {
    label: 'Crisis intelligence',
    color: 'rgb(255,122,154)',
    bg: 'linear-gradient(180deg, rgb(255,255,255) 0%, rgb(255,244,246) 100%)',
    card: '#ffffff',
    img: 'crisis.webp',
    alt: 'Emerging issue "Login failures": 34 mentions in 45 minutes, 11x velocity, mapped across Mumbai, Delhi and Bengaluru',
    title: 'See the crisis coming, hours before it trends',
    body: "A cluster of billing complaints accelerating at 3.4× normal volume, sentiment turning sharply, predicted to trend in ~4 hours, mapped across Mumbai, Delhi, and Bengaluru. That's the warning your PR and CX teams act on, not a wall of keyword alerts.",
  },
  {
    label: 'Influencer intelligence',
    color: BLUE,
    bg: 'linear-gradient(180deg, rgb(255,255,255) 0%, rgb(235,246,255) 100%)',
    card: 'rgb(240,249,255)',
    img: 'influencer.webp',
    alt: 'An influential voice routed to VIP handling',
    title: 'Know when an influential voice enters the story',
    body: 'When an account with 1.2M followers files a complaint, SignalSense flags it instantly so it can be routed to VIP handling before it shapes the narrative. Engage the voices that move the story, on time, every time.',
  },
  {
    label: 'Momentum intelligence',
    color: 'rgb(25,153,89)',
    bg: 'linear-gradient(180deg, rgb(255,255,255) 0%, rgb(240,252,245) 100%)',
    card: '#ffffff',
    img: 'momentum.webp',
    alt: 'Praise for a new feature is spiking',
    title: "Catch positive momentum while it's hot",
    body: "Praise for a new feature spiking? SignalSense detects the surge as it forms so marketing can amplify it at peak, turning organic love into reach instead of finding out in next week's report.",
  },
];

const FAQS = [
  ['What is SignalSense™?', "SignalSense™ is Locobuzz's proprietary AI signal intelligence engine. It reads the intent behind every brand conversation across social, review, and digital channels, labels it as a signal such as a billing complaint, a service delay, or influencer praise, and then watches for patterns. When the same signal clusters within a short window, SignalSense flags an emerging issue with severity scoring, geography, and a trend prediction, giving teams hours of head start before the issue trends."],
  ['How is SignalSense different from keyword alerts?', 'Keyword alerts match words, so they bury teams in noise and miss anything phrased differently. SignalSense reads intent, meaning it understands what each conversation is about even without the exact keyword. It filters spam automatically, scores severity, and surfaces only the ranked signals that matter. In a typical scenario, a keyword monitor produces 1,284 unprioritised alerts while SignalSense surfaces 1 ranked signal, around 4 hours earlier.'],
  ['How early can SignalSense detect a brand crisis?', 'SignalSense detects emerging issues at the clustering stage, typically at tens of mentions rather than thousands. For example, it can flag a login failure issue at 34 mentions in 45 minutes with an 11× acceleration rate, and predict when the issue is likely to trend. This gives PR, CX, and product teams hours of lead time to fix the root cause or respond before the story spreads.'],
  ['What signals does SignalSense detect beyond crises?', 'The same signal layer powers three moves: crisis aversion, influencer engagement, and momentum capture. SignalSense identifies when an influential voice raises a complaint so it can be routed to VIP handling, and detects when praise for a product or feature is spiking so marketing teams can amplify it while it is hot.'],
];

const eyebrow = (size = 15) => ({ fontSize: size, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: BLUE });
const h2 = { fontWeight: 600, color: INK, letterSpacing: '-1.2px', lineHeight: 1.08, fontSize: 'clamp(32px, 3.6vw, 52px)' };
const section = (top) => ({ maxWidth: 1360, margin: '0 auto', padding: `${top}px 40px 0` });
const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))` });

function Chip({ children, style, className }) {
  return (
    <div className={className} style={{ position: 'absolute', display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 59, color: '#fff', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', ...style }}>
      {children}
    </div>
  );
}

function Mini({ w1, w2, tag, tagBg, tagColor, faded, strike }) {
  const bar = (w) => <div style={{ height: 7, width: w, borderRadius: 4, background: BORDER }} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: faded ? 0.45 : 1 }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>{bar(w1)}{w2 && bar(w2)}</div>
      <span style={{ flex: 'none', borderRadius: 20, padding: '5px 11px', fontSize: 11, fontWeight: 600, background: tagBg, color: tagColor, textDecoration: strike ? 'line-through' : 'none' }}>{tag}</span>
    </div>
  );
}

function Row({ icon, label, color, bg, border }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, borderRadius: 9, padding: '6px 10px', background: bg, border: `1px solid ${border}` }}>
      <span style={{ flex: 'none', width: 16, textAlign: 'center', fontSize: 14, fontWeight: 700, lineHeight: 1, color }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>{label}</span>
    </div>
  );
}

const mockBox = { marginBottom: 28, borderRadius: 16, background: '#fff', border: `1px solid ${BORDER}`, boxShadow: '0 8px 24px rgba(0,0,0,0.051)', padding: 18, height: 210, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center' };
const featCard = { borderRadius: 25, padding: '40px 36px', background: 'linear-gradient(180deg, rgb(255,255,255) 0%, rgb(230,245,255) 100%)', border: `1px solid ${BORDER}` };
const featTitle = { margin: '0 0 16px', fontSize: 24, fontWeight: 700, letterSpacing: '-0.4px', color: INK };
const featBody = { margin: 0, fontSize: 16, lineHeight: '28px', color: BODY, textWrap: 'pretty' };

export default function SignalSenseClient() {
  const [open, setOpen] = useState(0);
  const volumeBars = [
    ['16%', BORDER], ['12%', BORDER], ['20%', BORDER], ['15%', BORDER], ['24%', BORDER], ['19%', BORDER],
    ['34%', 'rgba(47,137,255,0.35)'], ['52%', 'rgba(47,137,255,0.55)'], ['74%', 'rgb(255,150,157)'], ['100%', 'rgb(255,0,81)'],
  ];

  return (
    <div className={s.root}>
      {/* Hero */}
      <section data-screen-label="Hero" className={s.pad} style={{ position: 'relative', background: `#0B0A0E url('${IMG}/hero-bg.webp') center right / cover no-repeat`, padding: 'clamp(120px, 12vw, 160px) 40px 56px' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(11,10,14,0.92) 0%, rgba(11,10,14,0.55) 40%, rgba(11,10,14,0) 62%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1360, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 0.95fr)', gap: 40, alignItems: 'center', minHeight: 400 }} className={s.heroGrid}>
          <div>
            <h1 style={{ margin: '0 0 26px', fontSize: 'clamp(40px, 4.4vw, 58px)', lineHeight: 1.06, letterSpacing: '-1.4px', fontWeight: 600, color: '#fff', maxWidth: '13ch' }}>
              Every conversation is a <em className="serif-accent" style={{ fontStyle: 'italic', fontWeight: 400 }}>signal</em>
            </h1>
            <p style={{ margin: '0 0 38px', maxWidth: 629, fontSize: 16, lineHeight: '28px', color: 'rgba(255,255,255,0.8)', textWrap: 'pretty' }}>
              Signal Sense reads the intent behind every conversation and turns it into a signal then watches for the pattern. A complaint repeating, an influential voice rising, an issue forming. So you avert crises early, engage the right influencers, and catch new problems the moment they start not after they trend.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18 }}>
              <a href="#book-demo" className={s.btnPrimary} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 44, padding: '0 24px', borderRadius: 60, background: GRADIENT, color: '#fff', fontSize: 14, fontWeight: 500 }}>Talk to Customer Success</a>
              <a href="mailto:sales@locobuzz.com" className={s.btnGhost} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 44, padding: '0 22px', borderRadius: 54, border: '1px solid rgba(255,255,255,0.85)', color: '#fff', fontSize: 14, fontWeight: 500 }}>sales@locobuzz.com</a>
            </div>
          </div>

          <div className={s.heroVisual} style={{ position: 'relative', minHeight: 360 }} aria-hidden="true">
            <Chip className={s.float1} style={{ top: 120, left: 12, padding: '10px 18px', background: 'rgba(255,0,81,0.22)', border: '1px solid rgba(255,0,81,0.35)', fontSize: 15 }}>
              <span className={s.pulse} style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgb(255,0,81)' }} />Login failures
            </Chip>
            <Chip style={{ top: 168, left: 96, padding: '8px 15px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.22)', fontSize: 12 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.8)' }} />Billing Error
            </Chip>
            <Chip style={{ top: 200, left: -40, padding: '8px 15px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.22)', fontSize: 12 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.8)' }} />Product Query
            </Chip>
            <div style={{ position: 'absolute', bottom: 4, left: 0, width: '88%', minWidth: 220, maxWidth: 300, padding: '11px 13px 12px', borderRadius: 10, background: 'rgba(182,66,40,0.24)', border: '1px solid rgba(255,120,90,0.28)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
              <div style={{ marginBottom: 6, fontSize: 10, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#fff' }}>Emerging issue: &quot;Login failures&quot;</div>
              <p style={{ margin: '0 0 7px', fontSize: 11, lineHeight: 1.5, color: 'rgba(255,255,255,0.92)', textWrap: 'pretty' }}>The same signal is clustering fast. A new problem is forming. Flagged before it&apos;s a trend, so you can fix or respond first. Crisis averted.</p>
              <div style={{ textAlign: 'right', fontSize: 11, fontWeight: 900, color: '#fff' }}>34 mentions in 45 min ▲ 11×</div>
            </div>
          </div>
        </div>

        <div className={s.stats} style={{ position: 'relative', zIndex: 2, maxWidth: 1360, margin: '44px auto 0', borderRadius: 17, background: 'rgba(217,217,217,0.06)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', overflow: 'hidden' }}>
          {STATS.map(([big, small]) => (
            <div key={big} className={s.stat}>
              <div style={{ fontSize: 38, fontWeight: 700, color: '#fff', letterSpacing: '-0.6px' }}>{big}</div>
              <div style={{ marginTop: 10, fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.62)' }}>{small}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Signals, not keywords */}
      <section data-screen-label="Signals, not keywords" className={s.pad} style={section(118)}>
        <div style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto 58px' }}>
          <div style={eyebrow()}>Signals, not keywords</div>
          <h2 style={{ ...h2, margin: '16px 0 20px' }}>Label every conversation. Read the pattern. Act early.</h2>
          <p style={{ margin: 0, fontSize: 17, lineHeight: '30px', color: BODY, textWrap: 'pretty' }}>SignalSense turns each conversation into a signal, then aggregates those signals into the moves that matter: heading off a crisis, engaging the right influencer, riding a wave of momentum.</p>
        </div>
        <div style={{ ...grid(280), gap: 26 }}>
          <div style={featCard}>
            <div style={{ ...mockBox, gap: 12 }} aria-hidden="true">
              <Mini w1="100%" w2="62%" tag="Complaint" tagBg="rgba(255,0,81,0.10)" tagColor="rgb(255,0,81)" />
              <Mini w1="88%" w2="45%" tag="Praise" tagBg="rgba(25,153,89,0.12)" tagColor="rgb(25,153,89)" />
              <Mini w1="74%" w2="52%" tag="Influencer" tagBg="rgba(47,137,255,0.12)" tagColor={BLUE} />
              <Mini w1="56%" tag="Spam" tagBg="rgb(241,245,249)" tagColor="rgb(148,163,184)" faded strike />
            </div>
            <h3 style={featTitle}>Reads intent, not keywords</h3>
            <p style={featBody}>Every conversation is labelled with what it means: a complaint, a threat, praise, an influential voice. Nothing meaningful slips through a keyword gap, and the noise never gets through. Spam is filtered out automatically.</p>
          </div>

          <div style={featCard}>
            <div style={mockBox} aria-hidden="true">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'rgb(148,163,184)' }}>Signal volume</span>
                <span style={{ borderRadius: 20, padding: '5px 11px', fontSize: 11, fontWeight: 700, background: 'rgba(255,0,81,0.10)', color: 'rgb(255,0,81)' }}>▲ 11×</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 92 }}>
                {volumeBars.map(([h, bg], i) => <div key={i} style={{ flex: 1, height: h, borderRadius: 3, background: bg }} />)}
              </div>
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>34 mentions</span>
                <span style={{ fontSize: 12, color: 'rgb(148,163,184)' }}>in 45 min</span>
              </div>
            </div>
            <h3 style={featTitle}>Sees the pattern forming</h3>
            <p style={featBody}>When the same signal clusters in a short window, an emerging issue surfaces automatically with severity scoring, geography, and a trend prediction. You catch a new problem at 34 mentions, not 3,400. The earliest possible warning.</p>
          </div>

          <div style={featCard}>
            <div style={{ ...mockBox, alignItems: 'center', gap: 12 }} aria-hidden="true">
              <div style={{ width: '100%', borderRadius: 11, padding: '11px 14px', background: GRADIENT, color: '#fff', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>SignalSense™ signal layer</div>
              <div style={{ display: 'flex', gap: 6, color: 'rgb(148,163,184)', fontSize: 13, lineHeight: 1 }}><span>↓</span><span>↓</span><span>↓</span></div>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Row icon="!" label="Crisis" color="rgb(255,0,81)" bg="rgba(255,0,81,0.07)" border="rgba(255,0,81,0.16)" />
                <Row icon="★" label="Influencer" color={BLUE} bg="rgba(47,137,255,0.07)" border="rgba(47,137,255,0.18)" />
                <Row icon="↗" label="Momentum" color="rgb(25,153,89)" bg="rgba(25,153,89,0.07)" border="rgba(25,153,89,0.18)" />
              </div>
            </div>
            <h3 style={featTitle}>One radar, many moves</h3>
            <p style={featBody}>The same signals let you avert a brewing crisis, prioritise the influencers who shape the story, and ride positive momentum while it&apos;s hot. All from a single intelligence layer inside the Locobuzz platform.</p>
          </div>
        </div>
      </section>

      {/* Four steps */}
      <section data-screen-label="Four steps" className={s.pad} style={section(88)}>
        <div style={{ maxWidth: 940, margin: '0 0 34px' }}>
          <div style={eyebrow(14)}>The ORM game-changer</div>
          <h2 style={{ margin: '12px 0 0', fontSize: 'clamp(28px, 2.8vw, 40px)', lineHeight: 1.1, letterSpacing: '-1px', fontWeight: 600, color: INK }}>From raw mentions to a ranked signal in four steps</h2>
        </div>
        <div style={{ borderRadius: 28, overflow: 'hidden', border: `1px solid ${BORDER}`, background: '#0B0A0E', boxShadow: '0 20px 60px rgba(11,10,14,0.18)' }}>
          <img src={`${IMG}/funnel.webp`} width={2073} height={972} loading="lazy" decoding="async" alt="Raw mentions flow through SignalSense: listen everywhere, label the intent, detect the cluster, predict and prioritise into one ranked signal" style={{ display: 'block', width: '100%', height: 'auto' }} />
        </div>
        <div style={{ ...grid(200), marginTop: 34, gap: '30px 28px' }}>
          {STEPS.map(([n, title, body]) => (
            <div key={n}>
              <div style={{ marginBottom: 14, fontSize: 14, fontWeight: 700, letterSpacing: 2, color: BLUE }}>{n}</div>
              <h3 style={{ margin: '0 0 14px', fontSize: 19, fontWeight: 700, color: 'rgb(43,71,122)' }}>{title}</h3>
              <p style={{ margin: 0, fontSize: 14, lineHeight: '24px', color: BODY, textWrap: 'pretty' }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why teams switch */}
      <section data-screen-label="Why teams switch" className={s.pad} style={section(118)}>
        <div style={{ textAlign: 'center', maxWidth: 840, margin: '0 auto 58px' }}>
          <div style={eyebrow()}>Why teams switch</div>
          <h2 style={{ ...h2, margin: '16px 0 0' }}>Keyword monitors warn you too late. SignalSense <em className="serif-accent" style={{ fontStyle: 'italic', fontWeight: 400 }}>warns you first.</em></h2>
        </div>
        <div style={{ ...grid(320), gap: 26 }}>
          {[
            { name: 'Keyword monitoring', head: '1,284 alerts. Noise, no priority.', points: KEYWORD_POINTS, bg: 'rgba(217,217,217,0.36)', fg: 'rgb(31,48,74)', headFg: INK, text: 'rgb(51,65,85)', line: 'rgba(82,90,105,0.22)' },
            { name: 'SignalSense™', head: '1 ranked signal. 4 hours earlier.', points: SIGNAL_POINTS, bg: 'linear-gradient(122.56deg, rgb(74,83,243) 0%, rgb(0,178,255) 76.18%)', fg: '#fff', headFg: '#fff', text: 'rgba(255,255,255,0.95)', line: 'rgba(255,255,255,0.28)' },
          ].map((c) => (
            <div key={c.name} style={{ borderRadius: 28, padding: '38px 36px', background: c.bg, border: '1px solid rgba(82,90,105,0.33)' }}>
              <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', color: c.fg }}>{c.name}</div>
              <div style={{ margin: '16px 0 24px', fontSize: 22, fontWeight: 700, color: c.headFg }}>{c.head}</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {c.points.map((p) => <div key={p} style={{ padding: '16px 0', borderTop: `1px solid ${c.line}`, fontSize: 15, lineHeight: '24px', color: c.text }}>{p}</div>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Three moves */}
      <section data-screen-label="Three moves" className={s.pad} style={section(118)}>
        <div style={{ textAlign: 'center', maxWidth: 820, margin: '0 auto 58px' }}>
          <div style={eyebrow()}>Built for the moments that matter</div>
          <h2 style={{ ...h2, margin: '16px 0 0' }}>Three moves. One signal layer.</h2>
        </div>
        <div style={{ ...grid(280), gap: 26 }}>
          {MOVES.map((m) => (
            <div key={m.label} className={s.card} style={{ borderRadius: 24, border: `1px solid ${BORDER}`, background: m.card, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 26, background: m.bg }}>
                <img src={`${IMG}/${m.img}`} loading="lazy" decoding="async" alt={m.alt} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
              <div style={{ padding: '28px 30px 34px' }}>
                <div style={{ fontSize: 17, fontWeight: 600, color: m.color }}>{m.label}</div>
                <h3 style={{ margin: '12px 0 14px', fontSize: 23, lineHeight: 1.2, fontWeight: 600, color: '#000' }}>{m.title}</h3>
                <p style={{ margin: 0, fontSize: 15, lineHeight: '26px', color: BODY, textWrap: 'pretty' }}>{m.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section data-screen-label="FAQ" className={s.pad} style={{ margin: '118px 0 0', background: 'rgb(248,250,252)', padding: '96px 40px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={eyebrow()}>FAQ</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {FAQS.map(([q, a], i) => {
              const isOpen = open === i;
              return (
                <div key={q} style={{ borderRadius: 16, border: `1px solid ${BORDER}`, background: '#fff', overflow: 'hidden' }}>
                  <button type="button" className={s.faqBtn} aria-expanded={isOpen} aria-controls={`ss-faq-${i}`} onClick={() => setOpen(isOpen ? -1 : i)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, padding: '26px 30px', background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: 'rgb(15,23,42)' }}>{q}</span>
                    <span aria-hidden="true" style={{ fontSize: 26, fontWeight: 400, lineHeight: 1, color: BLUE }}>{isOpen ? '−' : '+'}</span>
                  </button>
                  {isOpen && <p id={`ss-faq-${i}`} className={s.faqAns} style={{ margin: 0, padding: '0 30px 30px', fontSize: 16, lineHeight: '30px', color: BODY, textWrap: 'pretty' }}>{a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-screen-label="Final CTA" className={s.pad} style={{ padding: '90px 40px 110px' }}>
        <div className={s.ctaBox} style={{ maxWidth: 1360, margin: '0 auto', borderRadius: 35, background: 'rgb(34,42,69)', padding: '92px 60px 96px', textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 26px', fontSize: 'clamp(32px, 3.6vw, 56px)', lineHeight: 1.1, letterSpacing: '-1.2px', fontWeight: 600, color: 'rgb(172,222,255)' }}>See the story before it&apos;s written.</h2>
          <p style={{ margin: '0 auto 44px', maxWidth: 900, fontSize: 'clamp(16px, 1.5vw, 20px)', lineHeight: 1.7, color: '#fff', textWrap: 'pretty' }}>Every conversation your brand has is already a signal. SignalSense™ reads them all, spots the pattern, and warns you hours before it trends. See it live on your own brand&apos;s data.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 22 }}>
            <a href="#book-demo" className={s.btnPrimary} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 30px', height: 52, borderRadius: 10, background: 'rgb(0,178,255)', color: '#000', fontSize: 17, fontWeight: 600 }}>Talk to Customer Success</a>
            <a href="mailto:sales@locobuzz.com" className={s.btnPrimary} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 30px', height: 52, borderRadius: 10, background: 'rgb(195,237,255)', color: '#000', fontSize: 17, fontWeight: 600 }}>sales@locobuzz.com</a>
          </div>
        </div>
      </section>
    </div>
  );
}
